// Push on this device (browser side): what the device can do, switch on/off.
import { api } from './api';
import { isIos, isStandalone } from './install';

export type PushState = 'unsupported' | 'ios-home-screen' | 'denied' | 'off' | 'on';

/** The service worker; gives up after a few seconds (blocked by the browser, private mode) instead of waiting forever */
async function worker() {
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Dieser Browser lässt keine Push-Nachrichten zu.')), 5000),
    ),
  ]);
}

/** iPhone/iPad: push only in the app added to the home screen; elsewhere it needs service worker + PushManager.
 *  "on" only when the server has this browser registered for the signed-in member (shared devices, re-login). */
export async function pushState(): Promise<PushState> {
  if (isIos() && !isStandalone()) return 'ios-home-screen';
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window))
    return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  const sub = await (await worker()).pushManager.getSubscription();
  if (!sub) return 'off';
  const { registered } = await api<{ registered: boolean }>('/push/check', {
    method: 'POST',
    body: { endpoint: sub.endpoint },
  });
  return registered ? 'on' : 'off';
}

const keyBytes = (b64url: string) => {
  const b64 = (b64url + '='.repeat((4 - (b64url.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(atob(b64), c => c.charCodeAt(0));
};

/** Asks for permission (only after a tap), subscribes and registers the device on the server */
export async function enablePush() {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Benachrichtigungen wurden nicht erlaubt.');
  const { publicKey } = await api<{ publicKey: string }>('/push/key');
  const reg = await worker();
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) }));
  const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  await api('/push/subscriptions', {
    method: 'POST',
    body: { subscription: { endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } } },
  });
}

export async function disablePush() {
  const reg = await worker();
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  await api('/push/subscriptions', { method: 'DELETE', body: { endpoint: sub.endpoint } });
  await sub.unsubscribe();
}

/** On logout: this device stops receiving the member's pushes (shared tablet, club PC). Never throws. */
export async function pushOffOnLogout() {
  try {
    if (!('serviceWorker' in navigator)) return;
    const sub = await (await worker()).pushManager.getSubscription();
    if (!sub) return;
    await api('/push/subscriptions', { method: 'DELETE', body: { endpoint: sub.endpoint } }).catch(() => {});
    await sub.unsubscribe();
  } catch {
    /* logging out must always work */
  }
}

/** All devices of the signed-in member off (lost phone); also this browser */
export async function allPushOff() {
  const r = await api<{ devices: number }>('/push/subscriptions/all', { method: 'DELETE' });
  try {
    const sub = await (await worker()).pushManager.getSubscription();
    await sub?.unsubscribe();
  } catch {
    /* only this browser's part */
  }
  return r.devices;
}
